import type { EditorLayer, LayerRenderPlane, TraversalState } from './types';
import { resolveLayerRenderPlane } from '../../../lib/levelEditorRenderPlane';
import { drawOcclusionForegroundPixels } from '../../../lib/levelEditorOcclusionMask';
import { isColorCell } from '../../../stores/editorStoreHelpers';
import { loadBrowserImage } from '../../../lib/browserImages';
import type { WorldAnnotation } from '../../../types/worldAnnotation';
export { downloadBlob } from '../../lib/fileDownloads';

const imageCache = new Map<string, Promise<HTMLImageElement>>();

interface PreloadLayerImagesOptions {
  includeAnimatedGifLayers?: boolean;
}

interface DrawDesignToCanvasOptions {
  clear?: boolean;
  renderPlane?: LayerRenderPlane;
}

const loadImage = (path: string): Promise<HTMLImageElement> => {
  const normalizedPath = path.trim();
  if (!normalizedPath) {
    return Promise.reject(new Error('Cannot render a layer image with a blank path.'));
  }

  const existing = imageCache.get(normalizedPath);
  if (existing) {
    return existing;
  }
  const request = new Promise<HTMLImageElement>((resolve, reject) => {
    void loadBrowserImage(normalizedPath)
      .then(resolve)
      .catch((error: unknown) => {
        imageCache.delete(normalizedPath);
        reject(
          error instanceof Error ? error : new Error(`Failed to load image: ${normalizedPath}`),
        );
      });
  });
  imageCache.set(normalizedPath, request);
  return request;
};

const toBlob = async (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Canvas export failed'));
        return;
      }
      resolve(blob);
    }, 'image/png');
  });

export const preloadLayerImages = async (
  layers: EditorLayer[],
  { includeAnimatedGifLayers = true }: PreloadLayerImagesOptions = {},
): Promise<Map<string, HTMLImageElement>> => {
  const uniquePaths = new Set<string>();
  for (const layer of layers) {
    if (!includeAnimatedGifLayers && layer.layerType === 'animated-gif') {
      continue;
    }
    for (const cell of Object.values(layer.tiles)) {
      if (isColorCell(cell)) {
        continue;
      }
      uniquePaths.add(cell.imagePath);
    }
  }

  const entries = await Promise.all(
    [...uniquePaths].map(async (path) => {
      const image = await loadImage(path);
      return [path, image] as const;
    }),
  );
  return new Map(entries);
};

export const drawDesignToCanvas = (
  canvas: HTMLCanvasElement,
  layers: EditorLayer[],
  imageByPath: Map<string, HTMLImageElement>,
  canvasWidth: number,
  canvasHeight: number,
  tileSize: number,
  options: DrawDesignToCanvasOptions = {},
): void => {
  const context = canvas.getContext('2d');
  if (!context) {
    return;
  }
  const renderPlane = options.renderPlane ?? 'background';
  const width = canvasWidth * tileSize;
  const height = canvasHeight * tileSize;
  if (canvas.width !== width) {
    canvas.width = width;
  }
  if (canvas.height !== height) {
    canvas.height = height;
  }

  if (options.clear !== false) {
    context.clearRect(0, 0, canvas.width, canvas.height);
  }
  context.imageSmoothingEnabled = false;

  for (const layer of layers) {
    if (
      layer.layerType === 'animated-gif' ||
      !layer.visible ||
      layer.opacity <= 0 ||
      resolveLayerRenderPlane(layer) !== renderPlane
    ) {
      continue;
    }
    context.globalAlpha = layer.opacity;
    for (const [key, cell] of Object.entries(layer.tiles)) {
      const [xRaw, yRaw] = key.split(',');
      const x = Number(xRaw);
      const y = Number(yRaw);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        continue;
      }
      if (isColorCell(cell)) {
        context.fillStyle = cell.color;
        context.fillRect(x * tileSize, y * tileSize, tileSize, tileSize);
        continue;
      }
      const image = imageByPath.get(cell.imagePath);
      if (!image) {
        continue;
      }
      context.drawImage(
        image,
        cell.sourceX,
        cell.sourceY,
        cell.tileSize,
        cell.tileSize,
        x * tileSize,
        y * tileSize,
        tileSize,
        tileSize,
      );
    }
  }
  context.globalAlpha = 1;
};

export const hasRenderableLayersInPlane = (
  layers: EditorLayer[],
  renderPlane: LayerRenderPlane,
): boolean =>
  layers.some(
    (layer) =>
      layer.layerType !== 'animated-gif' &&
      layer.visible &&
      layer.opacity > 0 &&
      resolveLayerRenderPlane(layer) === renderPlane &&
      Object.keys(layer.tiles).length > 0,
  );

export const createTraversalMaskCanvas = (
  canvasWidth: number,
  canvasHeight: number,
  tileSize: number,
  getTraversalAt: (x: number, y: number) => TraversalState,
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth * tileSize;
  canvas.height = canvasHeight * tileSize;
  const context = canvas.getContext('2d');
  if (!context) {
    return canvas;
  }
  for (let y = 0; y < canvasHeight; y += 1) {
    for (let x = 0; x < canvasWidth; x += 1) {
      context.fillStyle = getTraversalAt(x, y) === 'walkable' ? '#ffffff' : '#000000';
      context.fillRect(x * tileSize, y * tileSize, tileSize, tileSize);
    }
  }
  return canvas;
};

export const exportDesignCanvases = async (
  layers: EditorLayer[],
  canvasWidth: number,
  canvasHeight: number,
  tileSize: number,
  getTraversalAt: (x: number, y: number) => TraversalState,
  occlusionMaskUrl: string | null = null,
): Promise<{
  backgroundBlob: Blob;
  foregroundBlob: Blob;
  hasForegroundPixels: boolean;
  traversalBlob: Blob;
  backgroundCanvas: HTMLCanvasElement;
  foregroundCanvas: HTMLCanvasElement;
  traversalCanvas: HTMLCanvasElement;
}> => {
  const imageByPath = await preloadLayerImages(layers, { includeAnimatedGifLayers: false });
  const backgroundCanvas = document.createElement('canvas');
  drawDesignToCanvas(backgroundCanvas, layers, imageByPath, canvasWidth, canvasHeight, tileSize, {
    renderPlane: 'background',
  });
  const foregroundCanvas = document.createElement('canvas');
  const hasOcclusionForegroundPixels = await drawOcclusionForegroundPixels({
    backgroundCanvas,
    foregroundCanvas,
    occlusionMaskUrl,
  });
  drawDesignToCanvas(
    foregroundCanvas,
    layers,
    imageByPath,
    canvasWidth,
    canvasHeight,
    tileSize,
    hasOcclusionForegroundPixels
      ? { clear: false, renderPlane: 'foreground' }
      : { renderPlane: 'foreground' },
  );
  const hasForegroundPixels =
    hasOcclusionForegroundPixels || hasRenderableLayersInPlane(layers, 'foreground');
  const traversalCanvas = createTraversalMaskCanvas(
    canvasWidth,
    canvasHeight,
    tileSize,
    getTraversalAt,
  );
  const [backgroundBlob, foregroundBlob, traversalBlob] = await Promise.all([
    toBlob(backgroundCanvas),
    toBlob(foregroundCanvas),
    toBlob(traversalCanvas),
  ]);
  return {
    backgroundBlob,
    foregroundBlob,
    hasForegroundPixels,
    traversalBlob,
    backgroundCanvas,
    foregroundCanvas,
    traversalCanvas,
  };
};

export const createAnnotationsJsonBlob = (annotations: WorldAnnotation[]): Blob =>
  new Blob([`${JSON.stringify(annotations, null, 2)}\n`], { type: 'application/json' });
